import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useRestockMedicine } from '@/api/hooks/useMedicine';
import type { Medicine } from '@/api/types';
import { useSession } from '@/auth/session';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';

/**
 * Records a delivery as a new batch, distinct from editing the medicine's
 * bare stock number — this is what keeps FEFO and expiry tracking accurate
 * when the new units expire on a different date than what's already on hand.
 */
export function RestockDialog({
  medicine,
  open,
  onOpenChange,
}: {
  medicine: Medicine | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { can } = useSession();
  const canViewCosts = can(PERMISSIONS.medicinesViewCosts);
  const restock = useRestockMedicine();

  const [quantity, setQuantity] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && medicine) {
      setQuantity('');
      setBatchNo(medicine.batchNo);
      setExpiryDate(medicine.expiryDate.split('T')[0] ?? '');
      setCostPrice(medicine.costPrice != null ? String(medicine.costPrice) : '');
      setNote('');
      setError(null);
    }
  }, [open, medicine]);

  if (!medicine) return null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty < 1) {
      setError('Enter a quantity of at least 1.');
      return;
    }

    try {
      await restock.mutateAsync({
        id: medicine!.id,
        request: {
          quantity: qty,
          batchNo: batchNo.trim() || undefined,
          expiryDate: expiryDate || undefined,
          costPrice: costPrice ? Number(costPrice) : undefined,
          note: note.trim() || undefined,
        },
      });
      toast({
        title: 'Stock added',
        description: `${qty} ${medicine!.unit} added to ${medicine!.name}.`,
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Restock {medicine.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="rounded-md bg-secondary/50 px-3 py-2 text-sm text-muted-foreground">
            Currently {medicine.stock} {medicine.unit} on hand.
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="r-qty">Quantity received *</Label>
            <Input
              id="r-qty"
              type="number"
              min={1}
              autoFocus
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={`Units, in ${medicine.unit}`}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="r-batch">Batch no.</Label>
              <Input id="r-batch" value={batchNo} onChange={(e) => setBatchNo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="r-expiry">Expiry date</Label>
              <Input
                id="r-expiry"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>

          {canViewCosts && (
            <div className="space-y-1.5">
              <Label htmlFor="r-cost">Cost price (₹)</Label>
              <Input
                id="r-cost"
                type="number"
                min={0}
                step="0.01"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="r-note">Note</Label>
            <Input
              id="r-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — e.g. supplier invoice number"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={restock.isPending}>
              {restock.isPending && <LoaderCircle className="animate-spin" />}
              Add stock
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
